import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Alert,
  CircularProgress,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import { useAuthContext } from '../../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255, 255, 255, 0.06)',
    color: '#fff',
    borderRadius: '12px',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 0, 255, 0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#ff00ff', borderWidth: '2px' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#ff00ff' },
};

/**
 * Standalone login page – music pop pub vibe, MYM branding. No AuthModal.
 * After success → /mindyourmusic.
 */
const LoginPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, register, login, resumeGuestSession, requestPasswordReset, resendEmailVerification } = useAuthContext();
  const defaultRedirect = location.state?.from || '/mindyourmusic';

  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordStatus, setForgotPasswordStatus] = useState(null);
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('error');
  const [showTimeActive, setShowTimeActive] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/show-times/is-active`)
      .then((r) => r.json())
      .then((d) => setShowTimeActive(!!d.active))
      .catch(() => {});
  }, []);

  // Only auto-redirect when fully logged in (registered). Guests must stay on login when "login required" is ON to avoid redirect loop.
  const isGuest = user?.user_type === 'guest';
  useEffect(() => {
    if (isAuthenticated && !isGuest) navigate(defaultRedirect, { replace: true });
  }, [isAuthenticated, isGuest, defaultRedirect, navigate]);

  const handleToggleMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setAlertMessage('');
    setShowForgotPassword(false);
    setFormData({ fname: '', lname: '', email: '', password: '', confirmPassword: '' });
  };

  const handleInputChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (isLogin) {
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
    } else {
      if (!formData.fname) newErrors.fname = 'First name is required';
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setAlertMessage('');
    try {
      if (isLogin) {
        const response = await login(formData.email, formData.password);
        if (response.success) navigate(defaultRedirect, { replace: true });
      } else {
        const response = await register({
          fname: formData.fname,
          lname: formData.lname,
          email: formData.email,
          password: formData.password,
        });
        if (response.success) {
          setAlertMessage('Check your email to verify your account.');
          setAlertSeverity('success');
          setTimeout(() => { setIsLogin(true); setAlertMessage(''); }, 2500);
        }
      }
    } catch (err) {
      setAlertMessage(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    if (user?.user_type === 'guest') {
      navigate(defaultRedirect, { replace: true });
      return;
    }

    setIsLoading(true);
    setAlertMessage('');
    try {
      const response = await resumeGuestSession();
      if (response.success && response.exists) {
        navigate(defaultRedirect, { replace: true });
      } else {
        navigate('/guest-setup', { state: { from: defaultRedirect } });
      }
    } catch (err) {
      setAlertMessage(err.message);
      navigate('/guest-setup', { state: { from: defaultRedirect } });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectPath = encodeURIComponent(defaultRedirect);
    window.location.href = `${API_URL}/api/user-auth/auth/google?redirect=${redirectPath}`;
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
    setForgotPasswordEmail('');
    setForgotPasswordStatus(null);
    setErrors({});
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setForgotPasswordStatus(null);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      setErrors({ email: 'Email is required' });
      return;
    }
    setIsLoading(true);
    setErrors({});
    setForgotPasswordStatus(null);
    try {
      await requestPasswordReset(forgotPasswordEmail);
      setForgotPasswordStatus('success');
    } catch {
      setForgotPasswordStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setAlertMessage('Enter your email first');
      return;
    }
    setIsLoading(true);
    setAlertMessage('');
    try {
      await resendEmailVerification(formData.email);
      setAlertMessage('Verification email sent. Check your inbox.');
      setAlertSeverity('success');
    } catch (err) {
      setAlertMessage(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: [
          'radial-gradient(ellipse 100% 60% at 50% 0%, rgba(255,0,255,0.2) 0%, transparent 50%)',
          'radial-gradient(circle at 15% 85%, rgba(0,255,255,0.12) 0%, transparent 40%)',
          'radial-gradient(circle at 85% 15%, rgba(255,0,255,0.12) 0%, transparent 40%)',
          'linear-gradient(180deg, #0a0015 0%, #150025 35%, #1a0033 70%, #0a0015 100%)',
        ].join(', '),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: { xs: 2, sm: 3 },
        px: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Back to home */}
      {/* <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          color: 'rgba(255,255,255,0.8)',
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { color: '#ff00ff', bgcolor: 'rgba(255,0,255,0.1)' },
        }}
      >
        Back to home
      </Button> */}

      {/* Logo + tagline */}
      <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
        <Box
          component="img"
          src="/mind_your_music.png"
          alt="Mind Your Music"
          sx={{
            height: { xs: 56, sm: 72 },
            objectFit: 'contain',
            filter: 'drop-shadow(0 0 24px rgba(255,0,255,0.4))',
          }}
        />
        <Typography
          variant="h5"
          sx={{
            mt: 1,
            fontWeight: 700,
            background: 'linear-gradient(90deg, #ff00ff, #00ffff)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          Get in. Turn it up.
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
          Your music. Your night.
        </Typography>
      </Box>

      {/* Auth card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,0,255,0.25)',
          borderRadius: '20px',
          boxShadow: '0 0 40px rgba(255,0,255,0.15), inset 0 1px 0 rgba(255,255,255,0.05)',
          p: { xs: 2.5, sm: 3 },
        }}
      >
        {showForgotPassword ? (
          <>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
              Reset password
            </Typography>
            {forgotPasswordStatus === 'success' ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  Check your email for a reset link.
                </Typography>
                <Button onClick={handleBackToLogin} variant="outlined" sx={{ borderColor: '#ff00ff', color: '#ff00ff', '&:hover': { borderColor: '#ff00ff', bgcolor: 'rgba(255,0,255,0.1)' } }}>
                  ← Back to Sign In
                </Button>
              </Box>
            ) : forgotPasswordStatus === 'error' ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>We couldn&apos;t find that email.</Typography>
                <Button onClick={() => setForgotPasswordStatus(null)} sx={{ color: '#ff00ff' }}>Try again</Button>
                <Button onClick={handleBackToLogin} sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}>← Back</Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleForgotPasswordSubmit}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => { setForgotPasswordEmail(e.target.value); setErrors({}); }}
                  error={!!errors.email}
                  helperText={errors.email}
                  margin="normal"
                  sx={inputSx}
                  InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.5)' }} /> }}
                />
                <Button type="submit" fullWidth variant="contained" disabled={isLoading} sx={{ mt: 2, py: 1.5, borderRadius: '12px', background: 'linear-gradient(135deg, #ff00ff, #00ffff)', '&:hover': { background: 'linear-gradient(135deg, #00ffff, #ff00ff)' } }}>
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send reset link'}
                </Button>
                <Button fullWidth onClick={handleBackToLogin} sx={{ mt: 2, color: 'rgba(255,255,255,0.7)' }}>← Back to Sign In</Button>
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Sign In / Sign Up toggle */}
            <Stack direction="row" spacing={0} sx={{ mb: 3, p: 0.5, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '14px' }}>
              <Button
                fullWidth
                onClick={() => isLogin || handleToggleMode()}
                sx={{
                  py: 1.2,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  ...(isLogin ? { background: 'linear-gradient(135deg, #ff00ff, #00ffff)', color: '#fff', boxShadow: '0 4px 20px rgba(255,0,255,0.3)' } : { color: 'rgba(255,255,255,0.6)' }),
                }}
              >
                Sign In
              </Button>
              <Button
                fullWidth
                onClick={() => !isLogin || handleToggleMode()}
                sx={{
                  py: 1.2,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  ...(!isLogin ? { background: 'linear-gradient(135deg, #ff00ff, #00ffff)', color: '#fff', boxShadow: '0 4px 20px rgba(255,0,255,0.3)' } : { color: 'rgba(255,255,255,0.6)' }),
                }}
              >
                Sign Up
              </Button>
            </Stack>

            {alertMessage && (
              <Alert
                severity={alertSeverity}
                onClose={() => setAlertMessage('')}
                action={alertMessage.includes('verify') && isLogin ? <Button color="inherit" size="small" onClick={handleResendVerification} disabled={isLoading}>Resend</Button> : null}
                sx={{ mb: 2, borderRadius: '12px' }}
              >
                {alertMessage}
              </Alert>
            )}

            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                startIcon={<Box component="img" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" sx={{ width: 20, height: 20 }} />}
                sx={{ py: 1.4, borderRadius: '12px', borderColor: 'rgba(255,255,255,0.3)', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#ff00ff', bgcolor: 'rgba(255,0,255,0.08)' } }}
              >
                Google
              </Button>
              {!showTimeActive && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGuestLogin}
                  disabled={isLoading}
                  sx={{ py: 1.4, borderRadius: '12px', borderColor: 'rgba(255,255,255,0.3)', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#00ffff', bgcolor: 'rgba(0,255,255,0.08)' } }}
                >
                  Continue as Guest
                </Button>
              )}
            </Stack>
            {showTimeActive && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: '12px', bgcolor: 'rgba(0,255,255,0.08)', color: '#00ffff', '& .MuiAlert-icon': { color: '#00ffff' } }}>
                A show is live! Sign in with Google or email to join.
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', my: 2, '&::before, &::after': { content: '""', flex: 1, height: 1, bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <Typography variant="body2" sx={{ px: 2, color: 'rgba(255,255,255,0.5)' }}>or</Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit}>
              {!isLogin && (
                <Stack direction="row" spacing={2}>
                  <TextField fullWidth label="First name" value={formData.fname} onChange={handleInputChange('fname')} error={!!errors.fname} helperText={errors.fname} margin="normal" sx={inputSx} InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.5)' }} /> }} />
                  <TextField fullWidth label="Last name" value={formData.lname} onChange={handleInputChange('lname')} error={!!errors.lname} helperText={errors.lname} margin="normal" sx={inputSx} InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.5)' }} /> }} />
                </Stack>
              )}
              <TextField fullWidth label="Email" type="email" value={formData.email} onChange={handleInputChange('email')} error={!!errors.email} helperText={errors.email} margin="normal" sx={inputSx} InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.5)' }} /> }} />
              <TextField fullWidth label="Password" type="password" value={formData.password} onChange={handleInputChange('password')} error={!!errors.password} helperText={errors.password} margin="normal" sx={inputSx} InputProps={{ startAdornment: <LockIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.5)' }} /> }} />
              {!isLogin && (
                <TextField fullWidth label="Confirm password" type="password" value={formData.confirmPassword} onChange={handleInputChange('confirmPassword')} error={!!errors.confirmPassword} helperText={errors.confirmPassword} margin="normal" sx={inputSx} InputProps={{ startAdornment: <LockIcon sx={{ mr: 1, color: 'rgba(255,255,255,0.5)' }} /> }} />
              )}
              {isLogin && (
                <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                  <Link component="button" type="button" variant="body2" onClick={handleForgotPasswordClick} sx={{ color: 'rgba(255,0,255,0.9)' }}>Forgot password?</Link>
                </Box>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: 2.5,
                  mb: 1,
                  py: 1.5,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 24px rgba(255,0,255,0.35)',
                  '&:hover': { background: 'linear-gradient(135deg, #00ffff, #ff00ff)', boxShadow: '0 6px 28px rgba(255,0,255,0.45)' },
                  '&:disabled': { background: 'rgba(80,80,80,0.6)', color: 'rgba(255,255,255,0.5)' },
                }}
              >
                {isLoading ? <CircularProgress size={26} color="inherit" /> : isLogin ? 'Sign In' : 'Create account'}
              </Button>
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                {isLogin ? "Don't have an account? " : 'Already have an account? '}
                <Link component="button" type="button" onClick={handleToggleMode} sx={{ color: '#ff00ff', fontWeight: 600 }}>{isLogin ? 'Sign up' : 'Sign in'}</Link>
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Link component="button" type="button" variant="body2" onClick={() => navigate('/signup')} sx={{ mt: 2, color: 'rgba(255,255,255,0.5)' }}>
        New here? Create an account
      </Link>
    </Box>
  );
};

export default LoginPage;
